import React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { App, Form, Row, Col, Typography, Spin } from 'antd';
import { Navigate, useNavigate } from 'react-router';
import CardLayout from '../../layouts/CardLayout';
import { ContractorForm } from '../../components/Contractor/ContractorForm';
import { CONTRACTOR_CREATE } from '../../graphql/mutations/contractors';
import { MY_CONTRACTOR } from '../../graphql/queries/contractors';
import { useAuth } from '../../providers/AuthProvider';
import { ContractorFormValues } from '../../types/contractor';

const { Title, Paragraph } = Typography;

export const ContractorSetup: React.FC = () => {
  const { user, setAuthUser } = useAuth();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [form] = Form.useForm<ContractorFormValues>();

  const { data, loading: loadingContractor } = useQuery(MY_CONTRACTOR, {
    skip: !user?.roles?.includes('CONTRACTOR'),
    fetchPolicy: 'network-only',
  });

  const [createContractor, { loading }] = useMutation(CONTRACTOR_CREATE, {
    onCompleted: result => {
      const contractor = result?.contractorCreate;
      if (contractor && user) {
        message.success('Contractor profile created successfully');
        setAuthUser({ ...user, contractor });
        navigate('/', { replace: true });
      }
    },
    onError: error => {
      message.error(error.message || 'Failed to create contractor profile');
    },
  });

  if (!user?.roles?.includes('CONTRACTOR')) {
    return <Navigate to="/" replace />;
  }

  if (data?.myContractor && user && !user.contractor) {
    setAuthUser({ ...user, contractor: data.myContractor });
    return <Navigate to="/contractor/edit" replace />;
  }

  if (user?.contractor) {
    return <Navigate to="/contractor/edit" replace />;
  }

  if (loadingContractor) {
    return (
      <Row justify="center" style={{ width: '100%' }}>
        <Col>
          <Spin size="large" />
        </Col>
      </Row>
    );
  }

  const handleFinish = (values: ContractorFormValues) => {
    createContractor({
      variables: {
        input: {
          name: values.name,
          phone: values.phone,
          email: values.email,
          description: values.description,
          availableArea: values.availableArea,
        },
      },
    });
  };

  return (
    <Row justify="center" style={{ width: '100%', margin: '1em' }}>
      <Col xs={24} sm={20} md={14} lg={10}>
        <CardLayout>
          <Title level={3}>Complete Contractor Profile</Title>
          <Paragraph>
            Please provide your contractor company details. This information is required before you
            can continue using services.
          </Paragraph>
          <ContractorForm
            form={form}
            loading={loading}
            onFinish={handleFinish}
            submitText="Save"
            initialValues={{
              email: user.email,
            }}
          />
        </CardLayout>
      </Col>
    </Row>
  );
};
